import { HttpError } from './HttpError.js';
import type { TokenProvider, UnauthorizedHandler } from './tokenProvider.js';

/**
 * Returns the org id the platform admin is currently impersonating, or
 * undefined when not impersonating. Resolved per-request so a UI toggle
 * takes effect on the next call without rebuilding the client. Honored
 * by the server only for users in the configured ADMIN_ORGANIZATION;
 * non-admins receive a 403.
 */
export type ImpersonationProvider = () => string | undefined | Promise<string | undefined>;

export interface BaseClientOptions {
  baseUrl: string;
  getToken?: TokenProvider;
  onUnauthorized?: UnauthorizedHandler;
  fetch?: typeof fetch;
  debug?: boolean;
  defaultHeaders?: Record<string, string>;
  getImpersonateOrgId?: ImpersonationProvider;
  /**
   * fetch credentials mode. Defaults to 'include' so the shared-domain
   * `fb_session` cookie is stored on login and sent to /session/bootstrap —
   * including in dev, where the SPA origin (app.localtest.me:3001) and the
   * API (api.localtest.me:3000) are same-site but cross-origin. Server-side
   * (Node) fetch ignores it. Requires the API's CORS to allow credentials
   * for the exact origin (it does).
   */
  credentials?: RequestCredentials;
}

export type QueryValue = string | number | boolean | string[] | null | undefined;
export type Query = Record<string, QueryValue>;

export interface RequestOptions {
  /**
   * Query string params. Plain object; we serialize string/number/boolean,
   * repeat arrays, and drop undefined/null values.
   */
  query?: object;
  body?: unknown;
  headers?: Record<string, string>;
  /** Skip the bearer token (used for public endpoints like /user/login). */
  skipAuth?: boolean;
}

export class BaseClient {
  readonly baseUrl: string;
  private readonly getToken?: TokenProvider;
  private readonly onUnauthorized?: UnauthorizedHandler;
  private readonly fetchImpl: typeof fetch;
  private readonly debug: boolean;
  private readonly defaultHeaders: Record<string, string>;
  private readonly getImpersonateOrgId?: ImpersonationProvider;
  private readonly credentials: RequestCredentials;

  constructor(options: BaseClientOptions) {
    if (!options.baseUrl) throw new Error('CloudAgentClient: baseUrl is required');
    this.baseUrl = options.baseUrl.replace(/\/$/, '');
    this.getToken = options.getToken;
    this.onUnauthorized = options.onUnauthorized;
    this.fetchImpl = options.fetch ?? globalThis.fetch.bind(globalThis);
    this.debug = options.debug ?? false;
    this.defaultHeaders = options.defaultHeaders ?? {};
    this.getImpersonateOrgId = options.getImpersonateOrgId;
    this.credentials = options.credentials ?? 'include';
  }

  get<T>(path: string, options?: RequestOptions): Promise<T> {
    return this.request<T>('GET', path, options);
  }
  post<T>(path: string, options?: RequestOptions): Promise<T> {
    return this.request<T>('POST', path, options);
  }
  put<T>(path: string, options?: RequestOptions): Promise<T> {
    return this.request<T>('PUT', path, options);
  }
  patch<T>(path: string, options?: RequestOptions): Promise<T> {
    return this.request<T>('PATCH', path, options);
  }
  delete<T>(path: string, options?: RequestOptions): Promise<T> {
    return this.request<T>('DELETE', path, options);
  }

  async request<T>(
    method: string,
    path: string,
    options: RequestOptions = {},
    // Internal: set on the single post-refresh retry so a persistent 401
    // can't loop. Not part of the public surface.
    retrying = false,
  ): Promise<T> {
    const url = this.buildUrl(path, options.query);
    const headers: Record<string, string> = { ...this.defaultHeaders, ...(options.headers ?? {}) };

    if (!options.skipAuth && this.getToken) {
      const token = await this.getToken();
      if (token) headers['Authorization'] = `Bearer ${token}`;
    }

    // Impersonation header is opt-in via the constructor callback. The
    // explicit per-request `headers` win — call sites that already supply
    // an X-Impersonate-Org-Id (tests, edge cases) keep precedence.
    if (
      !options.skipAuth &&
      this.getImpersonateOrgId &&
      !('X-Impersonate-Org-Id' in headers) &&
      !('x-impersonate-org-id' in headers)
    ) {
      const target = await this.getImpersonateOrgId();
      if (target) headers['X-Impersonate-Org-Id'] = target;
    }

    let body: BodyInit | undefined;
    if (options.body !== undefined && options.body !== null) {
      if (options.body instanceof FormData) {
        body = options.body;
      } else {
        if (!('Content-Type' in headers) && !('content-type' in headers)) {
          headers['Content-Type'] = 'application/json';
        }
        body = JSON.stringify(options.body);
      }
    }

    if (this.debug) {
      // eslint-disable-next-line no-console
      console.debug('[flashbacktech/tsclient] →', method, url, options.body ?? '');
    }

    const res = await this.fetchImpl(url, { method, headers, body, credentials: this.credentials });

    let parsed: unknown = null;
    const text = await res.text();
    if (text.length > 0) {
      try {
        parsed = JSON.parse(text);
      } catch {
        parsed = text;
      }
    }

    if (this.debug) {
      // eslint-disable-next-line no-console
      console.debug('[flashbacktech/tsclient] ←', res.status, url, parsed);
    }

    if (!res.ok) {
      // On a 401 for an authed request, give the handler a chance to refresh
      // the access token, then retry the original request exactly once with
      // the new token. `retrying` guards against an infinite loop when the
      // refreshed token still 401s; `skipAuth` requests (login etc.) carry no
      // token to refresh, so a 401 there is a real failure, not staleness.
      if (res.status === 401 && this.onUnauthorized && !options.skipAuth && !retrying) {
        let refreshed = false;
        try {
          // void return ⇒ assume retry is worthwhile (legacy handlers);
          // explicit false ⇒ refresh failed, surface the original 401.
          refreshed = (await this.onUnauthorized()) !== false;
        } catch {
          // swallow handler failures — the original 401 is still surfaced
        }
        if (refreshed) {
          return this.request<T>(method, path, options, true);
        }
      }
      throw new HttpError(res.status, res.statusText, parsed);
    }

    return parsed as T;
  }

  private buildUrl(path: string, query?: object): string {
    const base = path.startsWith('http') ? path : `${this.baseUrl}${path.startsWith('/') ? path : `/${path}`}`;
    if (!query) return base;
    const params = new URLSearchParams();
    for (const [key, value] of Object.entries(query as Record<string, unknown>)) {
      if (value === undefined || value === null) continue;
      if (Array.isArray(value)) {
        for (const v of value) params.append(key, String(v));
      } else {
        params.set(key, String(value));
      }
    }
    const qs = params.toString();
    return qs ? `${base}?${qs}` : base;
  }
}
